import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteAdminSkill,
  listAdminSkills,
  patchAdminSkill,
  type AdminSkill,
  type AdminSkillList,
} from '@/api';
import SkillsTable from './components/SkillsTable';
import SkillDrawer from './components/SkillDrawer';
import { disciplineChips, ioTypeRows, adminListQuery } from './catalogBrowser';

type Mode = 'keyword' | 'graph';
type ActiveFilter = 'all' | 'true' | 'false';

const PAGE = 50;
const ACTIVE_CHIPS: { key: ActiveFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'true', label: 'Active' },
  { key: 'false', label: 'Inactive' },
];

export default function SkillsView({
  onToast,
  onError,
}: {
  onToast: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [mode, setMode] = useState<Mode>('keyword');
  const [active, setActive] = useState<ActiveFilter>('all');
  const [q, setQ] = useState('');
  const [produces, setProduces] = useState('');
  const [consumes, setConsumes] = useState('');
  const [concepts, setConcepts] = useState('');
  const [aroundId, setAroundId] = useState('');
  const [offset, setOffset] = useState(0);
  const [discipline, setDiscipline] = useState('');
  const [list, setList] = useState<AdminSkillList | null>(null);
  const [selected, setSelected] = useState<AdminSkill | null>(null);
  const [loading, setLoading] = useState(true);
  const paramsRef = useRef({ q, mode, active, produces, consumes, concepts, aroundId });
  paramsRef.current = { q, mode, active, produces, consumes, concepts, aroundId };

  const chips = disciplineChips(list?.disciplines);
  const ioRows = ioTypeRows(list?.io_types || []);

  const load = useCallback(
    async (nextOffset: number) => {
      const p = paramsRef.current;
      try {
        const query = adminListQuery({ discipline, q: p.q.trim() || undefined });
        const next = await listAdminSkills({
          ...query,
          mode: p.mode,
          active: p.active,
          produces: p.mode === 'graph' ? p.produces.trim() || undefined : undefined,
          consumes: p.mode === 'graph' ? p.consumes.trim() || undefined : undefined,
          concepts: p.mode === 'graph' ? p.concepts.trim() || undefined : undefined,
          around_id: p.mode === 'graph' ? p.aroundId.trim() || undefined : undefined,
          limit: PAGE,
          offset: nextOffset,
        });
        setList(next);
        setOffset(nextOffset);
      } catch (e) {
        onError(e instanceof Error ? e.message : 'failed');
      } finally {
        setLoading(false);
      }
    },
    [discipline, onError],
  );

  useEffect(() => {
    setLoading(true);
    void load(0);
  }, [mode, active, discipline, load]);

  const search = () => {
    setLoading(true);
    void load(0);
  };

  const replaceSkill = (next: AdminSkill) => {
    setList((prev) =>
      prev
        ? { ...prev, skills: prev.skills.map((s) => (s.id === next.id ? { ...s, ...next, body: undefined } : s)) }
        : prev,
    );
    setSelected((cur) => (cur && cur.id === next.id ? { ...cur, ...next } : cur));
  };

  const onTypeClick = (filter: { produces?: string; consumes?: string }) => {
    if (filter.produces) {
      setMode('graph');
      setProduces(filter.produces);
    } else if (filter.consumes) {
      setMode('graph');
      setConsumes(filter.consumes);
    }
    void load(0);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-textp">Skills</h1>
          <p className="text-sm text-texts mt-1">Search, rightsize, edit, and delete the catalog.</p>
        </div>
        <div className="flex items-center gap-2">
          {(['keyword', 'graph'] as Mode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`whitespace-nowrap text-sm px-3 py-1.5 rounded-full transition-colors ${
                mode === m ? 'bg-accent text-accent-text' : 'text-texts hover:text-textp'
              }`}
            >
              {m === 'keyword' ? 'Keyword' : 'Graph'}
            </button>
          ))}
        </div>
      </div>

      <form
        className="mt-5 flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
      >
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-texts text-sm" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={mode === 'keyword' ? 'Search skills...' : 'Optional text rank...'}
            className="w-full sm:w-96 bg-surface border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-textp placeholder:text-texts focus:outline-none focus:border-accent transition-colors"
          />
        </div>
        {mode === 'graph' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <input
              value={produces}
              onChange={(e) => setProduces(e.target.value)}
              placeholder="Produces"
              className="bg-surface border border-border rounded-lg px-3 py-2 text-sm text-textp"
            />
            <input
              value={consumes}
              onChange={(e) => setConsumes(e.target.value)}
              placeholder="Consumes"
              className="bg-surface border border-border rounded-lg px-3 py-2 text-sm text-textp"
            />
            <input
              value={concepts}
              onChange={(e) => setConcepts(e.target.value)}
              placeholder="Concepts"
              className="bg-surface border border-border rounded-lg px-3 py-2 text-sm text-textp"
            />
            <input
              value={aroundId}
              onChange={(e) => setAroundId(e.target.value)}
              placeholder="Around skill id"
              className="bg-surface border border-border rounded-lg px-3 py-2 text-sm text-textp"
            />
          </div>
        )}
        <button type="submit" className="hidden" aria-hidden />
      </form>

      {list && (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-xs uppercase tracking-widest text-texts">Total</p>
            <p className="mt-2 text-2xl font-mono text-textp">{list.total}</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-xs uppercase tracking-widest text-texts">Active</p>
            <p className="mt-2 text-2xl font-mono text-success">{list.active}</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-xs uppercase tracking-widest text-texts">Inactive</p>
            <p className="mt-2 text-2xl font-mono text-textp">{list.inactive}</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-xs uppercase tracking-widest text-texts">Empty I/O</p>
            <p className="mt-2 text-2xl font-mono text-textp">{list.empty_io}</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-xs uppercase tracking-widest text-texts">Page</p>
            <p className="mt-2 text-2xl font-mono text-textp">
              {list.offset + 1}–{list.offset + list.skills.length}
            </p>
          </div>
        </div>
      )}

      <div className="mt-5 flex items-center gap-1 mb-4">
        {chips.map((c) => (
          <button
            key={c.key}
            onClick={() => {
              setDiscipline(c.key);
              void load(0);
            }}
            className={`whitespace-nowrap text-sm px-3 py-1.5 rounded-full transition-colors ${
              discipline === c.key ? 'bg-accent text-accent-text' : 'text-texts hover:text-textp'
            }`}
          >
            {c.label} ({c.count})
          </button>
        ))}
      </div>

      <div className="mt-2 flex items-center gap-1 mb-4">
        {ACTIVE_CHIPS.map((f) => (
          <button
            key={f.key}
            onClick={() => setActive(f.key)}
            className={`whitespace-nowrap text-sm px-3 py-1.5 rounded-full transition-colors ${
              active === f.key ? 'bg-accent text-accent-text' : 'text-texts hover:text-textp'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {ioRows.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {ioRows.map((r) => (
            <button
              key={r.type}
              onClick={() => onTypeClick(r.filter)}
              className="text-xs px-2 py-1 rounded-md border border-border text-texts hover:text-textp hover:border-accent/50 transition-colors"
            >
              {r.type} (in {r.inputs} / out {r.outputs})
            </button>
          ))}
        </div>
      )}

      {loading && !list ? (
        <div className="text-sm text-texts">Loading…</div>
      ) : !list || list.skills.length === 0 ? (
        <div className="text-sm text-texts">No skills match.</div>
      ) : (
        <SkillsTable skills={list.skills} onSelect={setSelected} />
      )}

      {list && list.total > 0 && (
        <div className="mt-4 flex items-center gap-3">
          <button
            disabled={offset <= 0}
            onClick={() => {
              setLoading(true);
              void load(Math.max(0, offset - PAGE));
            }}
            className="text-sm px-3 py-1.5 rounded-lg border border-border text-texts disabled:opacity-40"
          >
            Previous
          </button>
          <button
            disabled={list.skills.length < PAGE}
            onClick={() => {
              setLoading(true);
              void load(offset + PAGE);
            }}
            className="text-sm px-3 py-1.5 rounded-lg border border-border text-texts disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      <SkillDrawer
        skill={selected}
        onClose={() => setSelected(null)}
        onSave={async (id, body) => {
          try {
            const next = await patchAdminSkill(id, body);
            replaceSkill(next);
            setList((prev) => (prev ? { ...prev, ...tallyAfterToggle(prev, next) } : prev));
            onToast('Skill updated');
          } catch (e) {
            onError(e instanceof Error ? e.message : 'failed');
          }
        }}
        onDelete={async (s) => {
          try {
            await deleteAdminSkill(s.id);
            setSelected(null);
            setLoading(true);
            await load(offset);
            onToast('Skill deleted');
          } catch (e) {
            onError(e instanceof Error ? e.message : 'failed');
          }
        }}
      />
    </div>
  );
}

function tallyAfterToggle(prev: AdminSkillList, next: AdminSkill): Pick<AdminSkillList, 'active' | 'inactive'> {
  const was = prev.skills.find((s) => s.id === next.id);
  if (!was || was.active === next.active) return { active: prev.active, inactive: prev.inactive };
  if (next.active) return { active: prev.active + 1, inactive: Math.max(0, prev.inactive - 1) };
  return { active: Math.max(0, prev.active - 1), inactive: prev.inactive + 1 };
}
